import { useContext, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { FormControl, FormHelperText, TextField } from "@material-ui/core"
import { FormikProvider, useFormik, Field } from "formik"

import { AppContext } from "../../context/AppContext"
import initialPersonalData from "../../helpers/configuration/initla-personal-data"
import validatePersonalData from "../../helpers/configuration/validation/validatePersonalData"
import useCountries from "../../hooks/use-countries"
import { useMergedViewImage } from "../../hooks/use-merged-view-image"
import useRequestSubmission from "../../hooks/use-request-submission"
import Loader from "../Loader/Loader"
import SelectForm from "../SelectForm/SelectForm"
import SubmissionResult from "../SubmissionResult/SubmissionResult"
import Button from "../buttons/Button"

import styles from "./RequestDataForm.module.scss"
import "./RequestDataForm.scss"

const RequestDataForm = ({ values, onSuccess, onError, closeModal }) => {
  const { t } = useTranslation()
  const appContext = useContext(AppContext)
  const { store } = appContext
  const params = useParams()
  const getLabel = key => t(key)

  const selectedModel = store?.chargers.find(c => c.code === params.MODELO)
  const model = selectedModel.code
  const currentView = "FRONTAL"
  const imageFolder = store.initConfig.images
  const { generateMergedImage } = useMergedViewImage({
    model: model,
    currentView,
    imageFolder,
  })

  const { submitRequest, resetSubmissionStatus } = useRequestSubmission({
    model,
    generateMergedImage,
  })
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [statusMessage, setStatusMessage] = useState(null)
  const [statusError, setStatusError] = useState(false)

  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === "true"

  const formik = useFormik({
    initialValues: initialPersonalData,
    validate: formValues => validatePersonalData(formValues, t),
    validateOnMount: true,
    onSubmit: async (formValues, { setSubmitting }) => {
      setStatusMessage(null)
      setStatusError(false)

      if (!executeRecaptcha) {
        console.error("Recaptcha not yet available")
        setSubmitting(false)
        return
      }

      try {
        const recaptchaToken =
          recaptchaEnabled && executeRecaptcha
            ? await executeRecaptcha("request_data_form")
            : null

        const res = await submitRequest({
          configuration: values,
          userValues: formValues,
          isFinalSubmit: true,
          recaptchaToken,
        })

        if (res.success) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: "generate_lead",
            contact_type: "ev_charger_form",
            contact_reason: "Contact us",
          })
          const msg = getLabel("Formulario enviado correctamente")
          setStatusMessage(msg)
          setStatusError(false)
          if (onSuccess) onSuccess(msg)
        } else {
          const msg = res.error?.message || getLabel("Ha ocurrido un error")
          setStatusMessage(msg)
          setStatusError(true)
          if (onError) onError(msg)
        }
      } catch (error) {
        const msg = error?.message || getLabel("Ha ocurrido un error")
        setStatusMessage(msg)
        setStatusError(true)
        if (onError) onError(msg)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { countriesOptions } = useCountries()

  const handleCloseResult = () => {
    setStatusMessage(null)
    setStatusError(false)
    resetSubmissionStatus()
    closeModal()
  }

  return (
    <>
      {statusMessage ? (
        <SubmissionResult
          error={statusError}
          title={statusError ? t("Error") : t("Thanks")}
          message={statusMessage}
          buttonText={t("close")}
          onButtonClick={handleCloseResult}
        />
      ) : (
        <FormikProvider value={formik}>
          <form className={styles.content} onSubmit={formik.handleSubmit}>
            <h2 className={styles.h2}>{t("formData.title")}</h2>
            <hr />

            <div className={styles.contentTop}>
              <div className={styles.col}>
                <TextField
                  className={styles.input}
                  id="name"
                  name="personal_data.first_name"
                  label={t("formData.name")}
                  variant="filled"
                  value={formik.values.personal_data.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.personal_data?.first_name &&
                    Boolean(formik.errors.personal_data?.first_name)
                  }
                  helperText={
                    formik.touched.personal_data?.first_name &&
                    formik.errors.personal_data?.first_name
                  }
                />
              </div>

              <div className={styles.col}>
                <TextField
                  className={styles.input}
                  id="lastName"
                  label={t("formData.LastName")}
                  variant="filled"
                  name="personal_data.last_name"
                  value={formik.values.personal_data.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.personal_data?.last_name &&
                    Boolean(formik.errors.personal_data?.last_name)
                  }
                  helperText={
                    formik.touched.personal_data?.last_name &&
                    formik.errors.personal_data?.last_name
                  }
                />
              </div>

              <div className={styles.col}>
                <TextField
                  className={styles.input}
                  id="empresa"
                  label={t("formData.Company")}
                  variant="filled"
                  name="personal_data.company"
                  value={formik.values.personal_data.company}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.personal_data?.company &&
                    Boolean(formik.errors.personal_data?.company)
                  }
                  helperText={
                    formik.touched.personal_data?.company &&
                    formik.errors.personal_data?.company
                  }
                />
              </div>

              <div className={styles.col}>
                <FormControl className={styles.input}>
                  <Field
                    name="personal_data.country"
                    component={SelectForm}
                    placeholder={t("formData.Country")}
                    options={countriesOptions}
                    onAfterChange={() =>
                      formik.setFieldValue("personal_data.province", "", true)
                    }
                  />
                  {formik.touched.personal_data?.country &&
                    formik.errors.personal_data?.country && (
                      <FormHelperText variant="filled" required error>
                        {formik.errors.personal_data?.country}
                      </FormHelperText>
                    )}
                </FormControl>
              </div>

              <div className={styles.col}>
                <TextField
                  className={styles.input}
                  id="email"
                  label={t("formData.email")}
                  variant="filled"
                  name="personal_data.email"
                  value={formik.values.personal_data.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.personal_data?.email &&
                    Boolean(formik.errors.personal_data?.email)
                  }
                  helperText={
                    formik.touched.personal_data?.email &&
                    formik.errors.personal_data?.email
                  }
                  type="email"
                />
              </div>

              <div className={styles.col}>
                <TextField
                  className={styles.input}
                  id="phone"
                  label={t("formData.Phone")}
                  variant="filled"
                  name="personal_data.phone"
                  value={formik.values.personal_data.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.personal_data?.phone &&
                    Boolean(formik.errors.personal_data?.phone)
                  }
                  helperText={
                    formik.touched.personal_data?.phone &&
                    formik.errors.personal_data?.phone
                  }
                  type="tel"
                />
              </div>
            </div>

            <div className={styles.contentBottom}>
              <div className={styles.col}>
                <TextField
                  variant="filled"
                  multiline
                  minRows={2}
                  aria-label={t("formData.querys")}
                  label={t("formData.querys")}
                  className={styles.textarea}
                  name="personal_data.observations"
                  value={formik.values.personal_data.observations}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.personal_data?.observations &&
                    Boolean(formik.errors.personal_data?.observations)
                  }
                  helperText={
                    formik.touched.personal_data?.observations &&
                    formik.errors.personal_data?.observations
                  }
                />
              </div>

              <div className={[styles.col, styles.consulta].join(" ")}>
                <div
                  className={[styles.contentOptions, styles.column].join(" ")}
                >
                  <label
                    className={[
                      styles.label,
                      formik.values.personal_data.personal_data_rights
                        ? styles.checked
                        : "",
                    ].join(" ")}
                  >
                    <span className={styles.checkmark}></span>
                    <span className={styles.labeltext}>
                      {t("formData.privacityText")} *
                      {formik.touched.personal_data?.personal_data_rights &&
                        formik.errors.personal_data?.personal_data_rights && (
                          <FormHelperText error>
                            {formik.errors.personal_data?.personal_data_rights}
                          </FormHelperText>
                        )}
                    </span>
                    <Field
                      className={styles.input}
                      type="checkbox"
                      name="personal_data.personal_data_rights"
                    />
                  </label>

                  <label
                    className={[
                      styles.label,
                      formik.values.personal_data.send_more_information
                        ? styles.checked
                        : "",
                    ].join(" ")}
                  >
                    <span className={styles.checkmark}></span>
                    <span className={styles.labeltext}>
                      {t("formData.MoreInformation")}
                    </span>
                    <Field
                      className={styles.input}
                      type="checkbox"
                      name="personal_data.send_more_information"
                    />
                  </label>
                </div>
              </div>

              <div className={styles.col}>
                <Button
                  type="submit"
                  primary={true}
                  disabled={formik.isSubmitting}
                >
                  {t("formData.Send")}
                </Button>
                {formik.isSubmitting && (
                  <div className={styles.submitting}>
                    <Loader />
                  </div>
                )}
              </div>
            </div>
          </form>
        </FormikProvider>
      )}
    </>
  )
}

export default RequestDataForm
